import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useProducts } from '../../hooks/useFirestore'
import { getCollection } from '../../services/firestore.service'
import { usePurchaseOrderAlerts } from '../../hooks/useAlerts'
import AlertCard from '../../components/purchaseOrders/AlertCard'

/**
 * Página AdminDashboard
 * Panel principal con estadísticas y resumen
 */
function AdminDashboard() {
  const { userData } = useAuth()
  const { products, loading: loadingProducts } = useProducts({})
  const { alerts, loading: loadingAlerts } = usePurchaseOrderAlerts()
  
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalUsers: 0,
    lowStock: 0
  })
  const [loading, setLoading] = useState(true)
  
  // Filtrar alertas por urgencia
  const highUrgencyAlerts = alerts.filter(a => a.urgency === 'high')
  const mediumUrgencyAlerts = alerts.filter(a => a.urgency === 'medium')
  const lowUrgencyAlerts = alerts.filter(a => a.urgency === 'low')

  // Calcular estadísticas
  useEffect(() => {
    const calculateStats = async () => {
      try {
        setLoading(true)

        // Contar productos
        const totalProducts = products.length

        // Contar productos con stock bajo (stock <= 5)
        const lowStock = products.filter(product => {
          const stock = product.stock || 0
          return stock > 0 && stock <= 5
        }).length

        // Obtener usuarios (si existe la colección)
        let totalUsers = 0
        try {
          const users = await getCollection('users', [], 'createdAt', 'desc')
          totalUsers = users.length
        } catch (error) {
          totalUsers = 0
        }

        // Obtener pedidos (si existe la colección)
        let totalOrders = 0
        try {
          const orders = await getCollection('orders', [], 'createdAt', 'desc')
          totalOrders = orders.length
        } catch (error) {
          totalOrders = 0
        }

        setStats({
          totalProducts,
          totalOrders,
          totalUsers,
          lowStock
        })
      } catch (error) {
        console.error('Error calculando estadísticas:', error)
      } finally {
        setLoading(false)
      }
    }

    if (!loadingProducts) {
      calculateStats()
    }
  }, [products, loadingProducts])

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h3 className="text-2xl font-light text-slate-900 dark:text-white">
            Bienvenido, <span className="font-semibold text-emerald-700 dark:text-emerald-400">{userData?.displayName || 'Administrador'}</span>
          </h3>
          <p className="text-slate-500 text-sm mt-1">Panel de control del sistema PinoHome. Gestión integral de su negocio.</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
              <span className="material-icons-round text-emerald-600">hourglass_top</span>
              Cargando estadísticas
            </h3>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-light text-slate-900 dark:text-white">
          Bienvenido, <span className="font-semibold text-emerald-700 dark:text-emerald-400">{userData?.displayName || 'Administrador'}</span>
        </h3>
        <p className="text-slate-500 text-sm mt-1">Panel de control del sistema PinoHome. Gestión integral de su negocio.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow group">
          <div className="flex justify-between items-start mb-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-700 dark:text-emerald-400">
              <span className="material-icons-round">inventory_2</span>
            </div>
            <span className="text-[10px] font-bold text-green-500 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded">+12%</span>
          </div>
          <p className="text-slate-500 text-sm font-medium">Productos</p>
          <div className="flex items-baseline gap-2 mt-1">
            <h4 className="text-3xl font-bold dark:text-white">{stats.totalProducts}</h4>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start mb-4">
            <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-amber-700 dark:text-amber-400">
              <span className="material-icons-round">shopping_cart</span>
            </div>
            <span className="text-[10px] font-bold text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded">Hoy</span>
          </div>
          <p className="text-slate-500 text-sm font-medium">Pedidos</p>
          <div className="flex items-baseline gap-2 mt-1">
            <h4 className="text-3xl font-bold dark:text-white">{stats.totalOrders}</h4>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start mb-4">
            <div className="w-10 h-10 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
              <span className="material-icons-round">people_alt</span>
            </div>
            <span className="text-[10px] font-bold text-slate-400 px-2 py-1">Activos</span>
          </div>
          <p className="text-slate-500 text-sm font-medium">Usuarios</p>
          <div className="flex items-baseline gap-2 mt-1">
            <h4 className="text-3xl font-bold dark:text-white">{stats.totalUsers}</h4>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border-l-4 border-l-rose-500 border-t border-r border-b border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start mb-4">
            <div className="w-10 h-10 rounded-lg bg-rose-50 dark:bg-rose-900/30 flex items-center justify-center text-rose-600 dark:text-rose-400">
              <span className="material-icons-round">warning</span>
            </div>
            <span className={`text-[10px] font-bold text-rose-500 bg-rose-50 dark:bg-rose-900/30 px-2 py-1 rounded ${stats.lowStock > 0 ? 'animate-pulse' : ''}`}>
              {stats.lowStock > 0 ? 'Crítico' : 'OK'}
            </span>
          </div>
          <p className="text-slate-500 text-sm font-medium">Stock Bajo</p>
          <div className="flex items-baseline gap-2 mt-1">
            <h4 className={`text-3xl font-bold ${stats.lowStock > 0 ? 'text-rose-600 dark:text-rose-400' : 'dark:text-white'}`}>
              {stats.lowStock}
            </h4>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
            <span className="material-icons-round text-emerald-600">bolt</span>
            Acciones Rápidas
          </h3>
          <Link to="/admin" className="text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:underline">Ver todas</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/productos/nuevo" className="flex flex-col items-center justify-center gap-4 p-8 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-emerald-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all group">
            <div className="w-12 h-12 rounded-full border-2 border-slate-100 dark:border-slate-600 flex items-center justify-center text-slate-400 group-hover:text-emerald-700 group-hover:border-emerald-600 transition-colors">
              <span className="material-icons-round text-2xl">add</span>
            </div>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Nuevo Producto</span>
          </Link>
          <Link to="/admin/pedidos" className="flex flex-col items-center justify-center gap-4 p-8 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-emerald-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all group">
            <div className="w-12 h-12 rounded-full border-2 border-slate-100 dark:border-slate-600 flex items-center justify-center text-slate-400 group-hover:text-emerald-700 group-hover:border-emerald-600 transition-colors">
              <span className="material-icons-round text-2xl">visibility</span>
            </div>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Ver Pedidos</span>
          </Link>
          <Link to="/admin/pedidos-compra/nuevo" className="flex flex-col items-center justify-center gap-4 p-8 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-emerald-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all group">
            <div className="w-12 h-12 rounded-full border-2 border-slate-100 dark:border-slate-600 flex items-center justify-center text-slate-400 group-hover:text-emerald-700 group-hover:border-emerald-600 transition-colors">
              <span className="material-icons-round text-2xl">local_mall</span>
            </div>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Nuevo Pedido Compra</span>
          </Link>
          <Link to="/admin/stock" className="flex flex-col items-center justify-center gap-4 p-8 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-emerald-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all group">
            <div className="w-12 h-12 rounded-full border-2 border-slate-100 dark:border-slate-600 flex items-center justify-center text-slate-400 group-hover:text-emerald-700 group-hover:border-emerald-600 transition-colors">
              <span className="material-icons-round text-2xl">equalizer</span>
            </div>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Gestionar Stock</span>
          </Link>
        </div>
      </div>

      {!loadingAlerts && alerts.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
              <span className="material-icons-round text-rose-500">report</span>
              Alertas de Pedidos de Compra
            </h3>
            <Link to="/admin/pedidos-compra" className="text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:underline">Ver todas</Link>
          </div>

          {highUrgencyAlerts.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-rose-600 dark:text-rose-400">Alta urgencia</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {highUrgencyAlerts.slice(0, 4).map(alert => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
              </div>
            </div>
          )}

          {mediumUrgencyAlerts.length > 0 && (
            <div className={`space-y-3 ${highUrgencyAlerts.length > 0 ? 'mt-6' : ''}`}>
              <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">Urgencia media</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {mediumUrgencyAlerts.slice(0, 4).map(alert => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
              </div>
            </div>
          )}

          {lowUrgencyAlerts.length > 0 && highUrgencyAlerts.length === 0 && mediumUrgencyAlerts.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Información</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {lowUrgencyAlerts.slice(0, 4).map(alert => (
                  <AlertCard key={alert.id} alert={alert} />
                ))}
              </div>
            </div>
          )}

          {alerts.length > 4 && (
            <div className="pt-6 mt-6 border-t border-slate-200 dark:border-slate-700 text-center">
              <Link to="/admin/pedidos-compra" className="text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:underline">
                Ver todas las alertas ({alerts.length})
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AdminDashboard

